import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Users, Search, Mail, Phone, UserCheck, UserX, Gift, X, CheckCircle, Clock, Copy } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Referral Modal ──────────────────────────────────────────────────────────

const ReferralModal = ({ student, token, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReferrals = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/users/${student.id}/referrals`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(res.data);
      } catch {
        toast.error('Failed to load referral data');
      } finally {
        setLoading(false);
      }
    };
    fetchReferrals();
  }, [student.id, token]);

  const copyCode = () => {
    navigator.clipboard.writeText(student.referral_code || '');
    toast.success('Referral code copied!');
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-[#731C1A] to-[#A83230] p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
              <Gift className="w-5 h-5 text-[#FACC15]" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-white text-lg leading-tight">{student.name}</h2>
              <p className="text-white/60 text-xs">Referral Program Details</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="text-white/70 hover:text-white hover:bg-white/10 rounded-lg">
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="p-6 space-y-3">
              {[1, 2, 3].map(i => <div key={i} className="h-14 bg-slate-100 rounded-xl animate-pulse" />)}
            </div>
          ) : data ? (
            <div className="p-6 space-y-5">
              {/* Referral Code */}
              <div className="bg-gradient-to-r from-secondary/20 to-secondary/5 border border-secondary/30 rounded-xl p-4">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Referral Code</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white px-4 py-2 rounded-lg font-mono text-xl font-bold text-primary tracking-widest text-center border border-secondary/40">
                    {data.student.referral_code || '—'}
                  </code>
                  {data.student.referral_code && (
                    <Button size="icon" variant="secondary" onClick={copyCode}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Stats Row */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-center">
                  <p className="font-heading text-2xl font-bold text-primary">{data.stats.total}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Total</p>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
                  <p className="font-heading text-2xl font-bold text-green-600">{data.stats.completed}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Completed</p>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-center">
                  <p className="font-heading text-2xl font-bold text-yellow-600">{data.stats.pending}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Pending</p>
                </div>
              </div>

              {/* Referral List */}
              <div>
                <h3 className="font-heading font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-3">
                  Referral History
                </h3>
                {data.referrals.length === 0 ? (
                  <div className="py-8 text-center">
                    <Gift className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
                    <p className="text-sm text-muted-foreground">No referrals made yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {data.referrals.map((r, idx) => (
                      <div
                        key={r.id || idx}
                        className="flex items-center justify-between p-3 bg-muted/50 rounded-xl border border-slate-100"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                            r.status === 'completed' ? 'bg-green-100' : 'bg-yellow-100'
                          }`}>
                            {r.status === 'completed'
                              ? <CheckCircle className="w-4 h-4 text-green-600" />
                              : <Clock className="w-4 h-4 text-yellow-600" />
                            }
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{r.referred_email}</p>
                            <p className="text-xs text-muted-foreground">
                              {r.created_at ? r.created_at.split('T')[0] : '—'}
                            </p>
                          </div>
                        </div>
                        <Badge className={
                          r.status === 'completed'
                            ? 'bg-green-100 text-green-700 border-0 shrink-0'
                            : 'bg-yellow-100 text-yellow-700 border-0 shrink-0'
                        }>
                          {r.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p className="p-6 text-center text-muted-foreground">No data available</p>
          )}
        </div>

        <div className="p-4 border-t">
          <Button variant="outline" className="w-full" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const AdminStudents = () => {
  const { token } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [selectedStudent, setSelectedStudent] = useState(null);

  const fetchStudents = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/users?role=student`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudents(response.data);
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchStudents(); }, [fetchStudents]);

  const toggleStatus = async (id, currentStatus) => {
    try {
      await axios.put(`${API_URL}/api/admin/users/${id}/status?is_active=${!currentStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Student ${currentStatus ? 'deactivated' : 'activated'}`);
      fetchStudents();
    } catch (error) { toast.error('Failed to update status'); }
  };

  const filteredStudents = students.filter(s => {
    const matchesSearch = s.name.toLowerCase().includes(search.toLowerCase()) || s.email.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = filter === 'all' || (filter === 'active' ? s.is_active : !s.is_active);
    return matchesSearch && matchesFilter;
  });

  return (
    <AdminLayout title="Students Management">
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search students..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="search-students" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Students</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="space-y-4">{[1, 2, 3, 4].map(i => <Card key={i} className="animate-pulse"><CardContent className="h-20"></CardContent></Card>)}</div>
      ) : filteredStudents.length > 0 ? (
        <div className="space-y-4">
          {filteredStudents.map((student) => (
            <Card key={student.id} data-testid={`student-${student.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold">{student.name}</h4>
                      <Badge className={student.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                        {student.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                      {student.referral_code && (
                        <Badge variant="outline" className="font-mono text-xs border-secondary/50 text-secondary-foreground bg-secondary/10">
                          {student.referral_code}
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mt-1">
                      <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{student.email}</span>
                      <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{student.phone}</span>
                      {student.qualification && <Badge variant="outline">{student.qualification}</Badge>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedStudent(student)}
                      className="text-primary border-primary/30 hover:bg-primary/5"
                      data-testid={`referrals-btn-${student.id}`}
                    >
                      <Gift className="w-4 h-4 mr-1.5" />
                      Referrals
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => toggleStatus(student.id, student.is_active)}>
                      {student.is_active ? <><UserX className="w-4 h-4 mr-2" />Deactivate</> : <><UserCheck className="w-4 h-4 mr-2" />Activate</>}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card><CardContent className="py-16 text-center"><Users className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" /><h3 className="font-semibold mb-2">No Students Found</h3><p className="text-muted-foreground">No students match your search criteria.</p></CardContent></Card>
      )}

      {selectedStudent && (
        <ReferralModal
          student={selectedStudent}
          token={token}
          onClose={() => setSelectedStudent(null)}
        />
      )}
    </AdminLayout>
  );
};

export default AdminStudents;
